import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Wallet as WalletIcon, ArrowLeft, Copy, Share2, Gift, Gamepad2, Trophy,
  ShoppingBag, Sparkles, TrendingUp, Loader2, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useShop } from "@/context/ShopContext";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";

const KIND_META = {
  order_earn: { label: "Order reward", icon: ShoppingBag },
  game_earn: { label: "Game reward", icon: Gamepad2 },
  referral: { label: "Referral", icon: Gift },
  milestone: { label: "Milestone", icon: TrendingUp },
  redeem: { label: "Redeemed", icon: Sparkles },
  unlock: { label: "Game unlock", icon: Trophy },
};

export default function Wallet() {
  const navigate = useNavigate();
  const { user, loading, signIn } = useAuth();
  const { shop } = useShop();
  const phone = user?.phone || "";

  const loyaltyQuery = useQuery({ queryKey: ["loyalty", phone], queryFn: () => api.getLoyalty(phone), enabled: !!phone });
  const ledgerQuery = useQuery({ queryKey: ["ledger", phone], queryFn: () => api.getLedger(phone), enabled: !!phone });
  const loyalty = loyaltyQuery.data;

  const earnedPoints = loyalty ? Math.max(0, loyalty.points - (loyalty.bonus_points || 0) - (loyalty.milestone_points || 0)) : 0;
  const refCode = loyalty?.referral_code || "";
  const shareUrl = useMemo(() => `${window.location.origin}/profile`, []);

  const shareRef = () => {
    const text = `Join me at ${shop?.name || "Dry Cleaners Goldys"}! Use my code ${refCode} for a welcome offer on your first order. ${shareUrl}`;
    if (navigator.share) navigator.share({ title: "Goldys referral", text }).catch(() => {});
    else { navigator.clipboard?.writeText(text); toast.success("Referral message copied — share it anywhere!"); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5 py-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-secondary" data-testid="wallet-back"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold"><WalletIcon className="h-6 w-6 text-primary" /> Points &amp; Wallet</h1>
      </div>

      {loading ? null : !user ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl bg-secondary p-8 text-center" data-testid="wallet-signed-out">
          <WalletIcon className="h-11 w-11 text-primary" />
          <p className="font-display text-lg font-bold">Sign in to view your wallet</p>
          <p className="text-sm text-muted-foreground">Track every point you earn and redeem in one place.</p>
          <button onClick={signIn} data-testid="wallet-signin" className="mt-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">Sign in with Google</button>
        </div>
      ) : !user.phone ? (
        <div className="rounded-2xl bg-secondary p-8 text-center">
          <p className="font-display text-lg font-bold">Add your phone number</p>
          <p className="text-sm text-muted-foreground">Add a mobile number in your profile to start your points wallet.</p>
          <Link to="/profile" className="mt-3 inline-block rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">Go to profile</Link>
        </div>
      ) : (
        <>
          {/* Balance hero */}
          <div className="rounded-2xl bg-primary p-5 text-primary-foreground" data-testid="wallet-balance">
            <p className="text-xs opacity-80">Total points balance</p>
            <p className="font-display text-4xl font-bold">{loyalty?.points ?? 0} <span className="text-lg font-semibold opacity-80">pts</span></p>
            <p className="mt-1 text-xs opacity-80">≈ {money(Math.round((loyalty?.points ?? 0) * (loyalty?.redeem_value_per_point ?? 1)))} off your next order · redeem at checkout</p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              {[
                ["Earned", earnedPoints, loyalty?.points_expires_at],
                ["Games", loyalty?.bonus_points || 0, loyalty?.bonus_points_expires_at],
                ["Milestone", loyalty?.milestone_points || 0, loyalty?.milestone_points_expires_at],
              ].map(([label, val, exp]) => (
                <div key={label} className="rounded-xl bg-white/15 p-2.5">
                  <p className="font-display text-xl font-bold">{Math.max(0, val)}</p>
                  <p className="text-[11px] opacity-85">{label}</p>
                  {exp ? <p className="text-[10px] opacity-70">exp {dayjs(exp).format("D MMM")}</p> : null}
                </div>
              ))}
            </div>
          </div>

          {/* Share to earn */}
          {refCode ? (
            <div className="rounded-2xl border border-border p-5" data-testid="wallet-share">
              <div className="flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /><h2 className="font-display text-sm font-bold">Share &amp; earn</h2></div>
              <p className="mt-1 text-xs text-muted-foreground">Invite friends — they get a welcome offer and you earn {shop?.referral_bonus_points || 100} points per referral.</p>
              <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-secondary px-4 py-3">
                <span className="font-display text-lg font-bold tracking-wider" data-testid="wallet-ref-code">{refCode}</span>
                <div className="flex gap-2">
                  <button onClick={() => { navigator.clipboard?.writeText(refCode); toast.success("Code copied"); }} data-testid="wallet-copy-code" className="rounded-full bg-card p-2"><Copy className="h-4 w-4" /></button>
                  <button onClick={shareRef} data-testid="wallet-share-button" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"><Share2 className="h-4 w-4" /> Share link</button>
                </div>
              </div>
            </div>
          ) : null}

          {/* History ledger */}
          <div className="rounded-2xl border border-border p-5" data-testid="wallet-ledger">
            <h2 className="flex items-center gap-2 font-display text-sm font-bold"><Clock className="h-4 w-4 text-primary" /> History</h2>
            <div className="mt-3">
              {ledgerQuery.isPending ? (
                <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : !ledgerQuery.data?.length ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No activity yet. Earn points by ordering, playing games or inviting friends!</p>
              ) : (
                <ul className="divide-y divide-border" data-testid="wallet-ledger-list">
                  {ledgerQuery.data.map((e, i) => {
                    const meta = KIND_META[e.kind] || { label: e.kind, icon: Sparkles };
                    const positive = e.amount > 0;
                    return (
                      <li key={i} className="flex items-center gap-3 py-3">
                        <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full", positive ? "bg-[hsl(var(--brand-tertiary))] text-primary" : "bg-secondary text-muted-foreground")}>
                          <meta.icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{e.reason || meta.label}</p>
                          <p className="text-xs text-muted-foreground">{dayjs(e.created_at).format("D MMM YYYY, h:mm A")}</p>
                        </div>
                        <span className={cn("font-display text-sm font-bold", positive ? "text-primary" : "text-muted-foreground")}>{positive ? "+" : ""}{e.amount}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <Link to="/games" className="flex items-center justify-center gap-2 rounded-2xl bg-secondary p-4 text-sm font-semibold" data-testid="wallet-play-cta">
            <Gamepad2 className="h-4 w-4 text-primary" /> Play games to earn more points
          </Link>
        </>
      )}
    </div>
  );
}
